import type { TemplateRegistration, TemplateData } from './types';
import { sanitizeText, sanitizeSeoText, escapeHtml, escapeAttr } from './sanitize';
import { getIconSvg, renderStars, buildFontsUrl, buildNavLinks, NavDataFlags } from './helpers';

const ALL_FEATURES = [
  'about-section', 'services-list', 'testimonials', 'contact-form',
  'phone-display', 'address-map', 'business-hours', 'social-links',
  'faq', 'price-list', 'promotions', 'photo-gallery', 'google-reviews',
];

export const techTerminal: TemplateRegistration = {
  meta: {
    id: 'tech-terminal',
    name: 'Tech Terminal',
    category: 'Modern',
    description: 'Developer-inspired terminal aesthetic with monospace fonts, code-block styling, and hacker vibes',
    previewImage: '/previews/tech-terminal.png',
    supportedFeatures: ALL_FEATURES,
    defaultEnabledFeatures: [
      'about-section', 'services-list', 'testimonials', 'contact-form',
      'phone-display', 'address-map', 'business-hours', 'social-links',
    ],
    themeEditable: false,
  },

  render(data: TemplateData, enabledFeatures: Set<string>): string {
    const b = data.businessName;
    const bSafe = sanitizeText(b);
    const headline = sanitizeText(data.headline);
    const aboutText = sanitizeText(data.aboutText);
    const seoTitle = sanitizeSeoText(data.seo?.title || b, 70);
    const seoDesc = sanitizeSeoText(data.seo?.description || data.headline, 160);
    const fontsUrl = buildFontsUrl(['JetBrains Mono', 'Fira Code']);

    const phone = data.businessData?.phone || '';
    const contactEmail = data.businessData?.contactEmail || null;
    const address = data.businessData?.address || '';
    const rating = data.businessData?.rating || 0;
    const reviewCount = data.businessData?.reviewCount || 0;
    const hours = data.businessData?.businessHours || [];
    const photos = data.businessData?.photos || [];
    const mapsUrl = data.businessData?.googleMapsUrl || '';
    const galleryPhotos = photos.slice(1, 8);
    const navDataFlags: NavDataFlags = {
      hasServices: data.services.length > 0,
      hasTestimonials: data.testimonials.length > 0,
      hasGalleryPhotos: galleryPhotos.length > 0,
      hasFaq: !!(data.faq && data.faq.length > 0),
      hasPriceList: !!(data.priceList && data.priceList.length > 0),
    };
    const navLinks = buildNavLinks(enabledFeatures, navDataFlags);

    const navHtml = navLinks.map(l =>
      `<a href="${escapeAttr(l.href)}" class="nav-link">${escapeHtml(l.label)}</a>`
    ).join('');

    const promotionHtml = enabledFeatures.has('promotions') && data.promotion ? `
      <div class="promo-banner">
        <span class="promo-prefix">[ALERT]</span>
        <span class="promo-discount">${sanitizeText(data.promotion.discount)}</span>
        <span class="promo-title">${sanitizeText(data.promotion.title)}</span>
        &mdash; <span class="promo-desc">${sanitizeText(data.promotion.description)}</span>
      </div>` : '';

    const heroPhoto = photos[0] || '';
    const heroHtml = `
      <section class="hero">
        ${heroPhoto ? `<div class="hero-bg-img" style="background-image:url('${escapeAttr(heroPhoto)}')"></div>` : ''}
        <div class="hero-strips"></div>
        <div class="hero-inner-content">
          <div class="terminal-window">
            <div class="terminal-bar">
              <span class="dot red"></span>
              <span class="dot yellow"></span>
              <span class="dot green"></span>
              <span class="terminal-title">${bSafe} &mdash; bash</span>
            </div>
            <div class="terminal-body">
              <div class="terminal-line"><span class="prompt">$</span> cat welcome.txt</div>
              <h1 class="hero-headline">${headline}<span class="cursor">_</span></h1>
              <div class="terminal-line"><span class="prompt">$</span> echo $COMPANY</div>
              <p class="hero-sub">${bSafe}</p>
              ${enabledFeatures.has('phone-display') && phone ? `
              <div class="terminal-line"><span class="prompt">$</span> echo $PHONE</div>
              <p class="hero-detail">${sanitizeText(phone)}</p>` : ''}
              ${enabledFeatures.has('google-reviews') && rating > 0 ? `
              <div class="terminal-line"><span class="prompt">$</span> echo "$RATING / 5.0 (${reviewCount} reviews)"</div>
              <div class="hero-rating">${renderStars(rating)} <span class="rating-text">${rating.toFixed(1)}</span></div>` : ''}
              <div class="terminal-line dim"><span class="prompt">$</span> <span class="cursor-blink">_</span></div>
            </div>
          </div>
        </div>
      </section>`;

    const aboutHtml = enabledFeatures.has('about-section') ? `
      <section id="about" class="section">
        <div class="section-header">
          <span class="section-prefix">#</span>
          <h2 class="section-title">README.md</h2>
        </div>
        <div class="code-block">
          <div class="code-header"><span>about.txt</span></div>
          <div class="code-body">
            <p>${aboutText}</p>
            <div class="about-stats">
              ${rating > 0 ? `<div class="stat-block"><span class="stat-value">${rating.toFixed(1)}</span><span class="stat-label">Rating</span></div>` : ''}
              ${reviewCount > 0 ? `<div class="stat-block"><span class="stat-value">${reviewCount}</span><span class="stat-label">Reviews</span></div>` : ''}
              <div class="stat-block"><span class="stat-value">${data.services.length}</span><span class="stat-label">Services</span></div>
            </div>
          </div>
        </div>
      </section>` : '';

    const servicesHtml = enabledFeatures.has('services-list') && data.services.length > 0 ? `
      <section id="services" class="section">
        <div class="section-header">
          <span class="section-prefix">#</span>
          <h2 class="section-title">services --list</h2>
        </div>
        <div class="code-block">
          <div class="code-header"><span>services.log</span></div>
          <div class="code-body">
            <div class="services-list">
              ${data.services.map(s => `
                <div class="service-row">
                  <div class="service-icon">${getIconSvg(s.icon || 'star')}</div>
                  <div class="service-text">
                    <h3>${sanitizeText(s.name)}</h3>
                    <div class="terminal-line"><span class="prompt">&gt;</span> ${sanitizeText(s.description)}</div>
                    ${s.price ? `<div class="terminal-line"><span class="prompt">$</span> <span class="service-price">${sanitizeText(s.price)}</span></div>` : ''}
                  </div>
                </div>
              `).join('')}
            </div>
          </div>
        </div>
      </section>` : '';

    const testimonialsHtml = enabledFeatures.has('testimonials') && data.testimonials.length > 0 ? `
      <section id="testimonials" class="section">
        <div class="section-header">
          <span class="section-prefix">#</span>
          <h2 class="section-title">reviews --all</h2>
        </div>
        <div class="testimonials-stack">
          ${data.testimonials.map((t, i) => `
            <div class="testimonial-card code-block ${i % 2 === 0 ? 'align-left' : 'align-right'}">
              <div class="code-header">
                <span>${sanitizeText(t.name)}</span>
                ${t.role ? `<span class="testimonial-role">${sanitizeText(t.role)}</span>` : ''}
              </div>
              <div class="code-body">
                <div class="terminal-line"><span class="prompt output">&gt;</span> "${sanitizeText(t.content)}"</div>
                ${t.rating ? `<div class="testimonial-stars">${renderStars(t.rating)}</div>` : ''}
              </div>
            </div>
          `).join('')}
        </div>
      </section>` : '';

    const galleryHtml = enabledFeatures.has('photo-gallery') && photos.length > 1 ? `
      <section id="gallery" class="section">
        <div class="section-header">
          <span class="section-prefix">#</span>
          <h2 class="section-title">gallery --view</h2>
        </div>
        <div class="gallery-grid">
          ${photos.slice(0, 8).map(p => `
            <div class="gallery-item">
              <img src="${escapeAttr(p)}" alt="${escapeAttr(b)} photo" loading="lazy" />
            </div>
          `).join('')}
        </div>
      </section>` : '';

    const faqHtml = enabledFeatures.has('faq') && data.faq && data.faq.length > 0 ? `
      <section id="faq" class="section">
        <div class="section-header">
          <span class="section-prefix">#</span>
          <h2 class="section-title">faq --help</h2>
        </div>
        <div class="faq-list">
          ${data.faq.map(f => `
            <details class="faq-item code-block">
              <summary class="code-header faq-question">
                <span class="prompt">&gt;</span> ${sanitizeText(f.question)}
              </summary>
              <div class="code-body faq-answer">${sanitizeText(f.answer)}</div>
            </details>
          `).join('')}
        </div>
      </section>` : '';

    const priceHtml = enabledFeatures.has('price-list') && data.priceList && data.priceList.length > 0 ? `
      <section id="pricing" class="section">
        <div class="section-header">
          <span class="section-prefix">#</span>
          <h2 class="section-title">pricing --table</h2>
        </div>
        <div class="code-block">
          <div class="code-header"><span>pricing.csv</span></div>
          <div class="code-body price-table">
            ${data.priceList.map(p => `
              <div class="price-row">
                <span class="price-name">${sanitizeText(p.name)}</span>
                <span class="price-sep">................</span>
                <span class="price-amount">${sanitizeText(p.price)}</span>
              </div>
              <div class="price-desc"><span class="prompt dim">#</span> ${sanitizeText(p.description)}</div>
            `).join('')}
          </div>
        </div>
      </section>` : '';

    const hoursHtml = enabledFeatures.has('business-hours') && hours.length > 0 ? `
      <section class="section">
        <div class="section-header">
          <span class="section-prefix">#</span>
          <h2 class="section-title">hours --schedule</h2>
        </div>
        <div class="code-block">
          <div class="code-header"><span>schedule.log</span></div>
          <div class="code-body">
            ${hours.map(h => `<div class="terminal-line"><span class="prompt">&gt;</span> ${sanitizeText(h)}</div>`).join('')}
          </div>
        </div>
      </section>` : '';

    const contactHtml = enabledFeatures.has('contact-form') ? `
      <section id="contact" class="section">
        <div class="section-header">
          <span class="section-prefix">#</span>
          <h2 class="section-title">contact --send</h2>
        </div>
        <div class="contact-grid">
          <div class="contact-info code-block">
            <div class="code-header"><span>contact-info.txt</span></div>
            <div class="code-body">
              ${enabledFeatures.has('phone-display') && phone ? `<div class="terminal-line"><span class="prompt">PHONE:</span> ${sanitizeText(phone)}</div>` : ''}
              ${enabledFeatures.has('address-map') && address ? `<div class="terminal-line"><span class="prompt">ADDR:</span> ${sanitizeText(address)}</div>` : ''}
              ${enabledFeatures.has('address-map') && mapsUrl ? `<div class="terminal-line"><a href="${escapeAttr(mapsUrl)}" target="_blank" rel="noopener" class="map-link"><span class="prompt">MAP:</span> Open in Google Maps</a></div>` : ''}
            </div>
          </div>
          <form class="contact-form" ${contactEmail ? `action="https://formsubmit.co/${escapeAttr(contactEmail)}" method="POST"` : 'onsubmit="return false;"'} style="max-width:100%;">
            <div class="form-group">
              <label class="form-label">name:</label>
              <input type="text" class="form-input" placeholder="your_name" required />
            </div>
            <div class="form-group">
              <label class="form-label">email:</label>
              <input type="email" class="form-input" placeholder="user@domain.com" required />
            </div>
            <div class="form-group">
              <label class="form-label">message:</label>
              <textarea class="form-textarea" rows="5" placeholder="Enter your message..." required></textarea>
            </div>
            <button type="submit" class="form-btn">$ send --message</button>
          </form>
        </div>
      </section>` : '';

    const footerHtml = `
      <footer class="site-footer">
        <div class="footer-inner">
          <div class="terminal-line"><span class="prompt">$</span> echo "${bSafe}"</div>
          ${enabledFeatures.has('social-links') ? `
          <div class="social-links">
            <a href="#" class="social-link">[facebook]</a>
            <a href="#" class="social-link">[twitter]</a>
            <a href="#" class="social-link">[instagram]</a>
            <a href="#" class="social-link">[github]</a>
          </div>` : ''}
          <p class="footer-copy">Process exited with code 0</p>
        </div>
      </footer>`;

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${seoTitle}</title>
  <meta name="description" content="${seoDesc}" />
  <link rel="preconnect" href="https://fonts.googleapis.com" />
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
  <link href="${fontsUrl}" rel="stylesheet" />
  <style>
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

    :root {
      --bg: #0f172a;
      --bg-card: #1e293b;
      --bg-header: #0c1222;
      --green: #22c55e;
      --blue: #3b82f6;
      --text: #e2e8f0;
      --muted: #94a3b8;
      --border: #334155;
      --heading-font: 'JetBrains Mono', 'Courier New', monospace;
      --body-font: 'Fira Code', 'Courier New', monospace;
    }

    html { scroll-behavior: smooth; }

    body {
      font-family: var(--body-font);
      color: var(--text);
      background: var(--bg);
      line-height: 1.7;
      font-size: 14px;
      -webkit-font-smoothing: antialiased;
      position: relative;
    }

    /* SCANLINE OVERLAY */
    body::after {
      content: '';
      position: fixed; inset: 0; z-index: 9999;
      pointer-events: none;
      background: repeating-linear-gradient(
        0deg,
        transparent,
        transparent 2px,
        rgba(0,0,0,0.03) 2px,
        rgba(0,0,0,0.03) 4px
      );
    }

    /* NAV */
    .site-nav {
      position: sticky; top: 0; z-index: 100;
      background: var(--bg-header);
      border-bottom: 1px solid var(--border);
      padding: 0 24px;
    }
    .nav-inner {
      max-width: 1100px; margin: 0 auto;
      display: flex; align-items: center; justify-content: space-between;
      height: 48px;
    }
    .nav-brand {
      font-family: var(--heading-font); font-size: 0.95rem;
      color: var(--green); text-decoration: none; font-weight: 700;
    }
    .nav-brand::before { content: '> '; opacity: 0.5; }
    .nav-links { display: flex; gap: 20px; }
    .nav-toggle { display: none; background: none; border: none; cursor: pointer; }
    .nav-toggle span { display: block; width: 22px; height: 2px; background: var(--green); margin: 5px 0; }
    .nav-link {
      font-family: var(--body-font); font-size: 0.8rem;
      color: var(--muted); text-decoration: none;
      transition: color 0.2s;
    }
    .nav-link:hover { color: var(--green); }
    .nav-link::before { content: './'; color: var(--border); }

    /* PROMO */
    .promo-banner {
      background: var(--green); color: var(--bg);
      text-align: center; padding: 8px 24px; font-size: 0.85rem;
      font-weight: 600;
    }
    .promo-prefix { opacity: 0.7; margin-right: 8px; }

    /* TERMINAL WINDOW */
    .terminal-window {
      background: var(--bg-card); border: 1px solid var(--border);
      border-radius: 8px; overflow: hidden;
    }
    .terminal-bar {
      background: var(--bg-header); padding: 10px 16px;
      display: flex; align-items: center; gap: 8px;
      border-bottom: 1px solid var(--border);
    }
    .dot {
      width: 12px; height: 12px; border-radius: 50%;
    }
    .dot.red { background: #ef4444; }
    .dot.yellow { background: #eab308; }
    .dot.green { background: #22c55e; }
    .terminal-title {
      margin-left: 8px; font-size: 0.75rem; color: var(--muted);
    }
    .terminal-body { padding: 24px; }

    /* HERO - overlay strips / scan-line effect */
    .hero {
      position: relative; min-height: 80vh; display: flex; align-items: center;
      overflow: hidden; background: var(--bg-card);
    }
    .hero-bg-img { position: absolute; inset: 0; background-size: cover; background-position: center; opacity: .2; }
    .hero-strips { position: absolute; inset: 0; z-index: 1; pointer-events: none;
      background: repeating-linear-gradient(
        0deg,
        transparent 0px,
        transparent 6px,
        rgba(34,197,94,.06) 6px,
        rgba(34,197,94,.06) 8px,
        transparent 8px,
        transparent 24px
      );
    }
    .hero-strips::before { content: ''; position: absolute; top: 20%; left: 0; right: 0; height: 2px; background: rgba(34,197,94,.15); }
    .hero-strips::after { content: ''; position: absolute; top: 55%; left: 0; right: 0; height: 2px; background: rgba(59,130,246,.15); }
    .hero-inner-content { position: relative; z-index: 2; max-width: 900px; margin: 0 auto; padding: 100px 24px 60px; width: 100%; }
    .hero-headline {
      font-family: var(--heading-font);
      font-size: 2rem; font-weight: 700; color: var(--green);
      margin: 8px 0; line-height: 1.3;
    }
    .hero-sub {
      color: var(--blue); font-size: 1rem; margin: 4px 0 16px;
    }
    .hero-detail { color: var(--muted); margin: 4px 0 12px; }
    .hero-rating { display: flex; align-items: center; gap: 8px; margin: 4px 0; }
    .star.filled { color: #eab308; }
    .star { color: var(--border); }
    .rating-text { color: var(--muted); font-size: 0.85rem; }

    .terminal-line {
      margin: 4px 0; color: var(--muted); font-size: 0.9rem;
    }
    .prompt { color: var(--green); font-weight: 700; margin-right: 8px; }
    .prompt.output { color: var(--blue); }
    .dim { opacity: 0.4; }

    /* CURSOR ANIMATIONS */
    .cursor {
      animation: blink-cursor 1s step-end infinite;
      color: var(--green);
    }
    .cursor-blink {
      animation: blink-cursor 1s step-end infinite;
      color: var(--green);
    }
    @keyframes blink-cursor {
      0%, 100% { opacity: 1; }
      50% { opacity: 0; }
    }

    /* SECTIONS */
    .section {
      max-width: 900px; margin: 0 auto; padding: 40px 24px;
    }
    .section-header {
      display: flex; align-items: center; gap: 10px;
      margin-bottom: 20px;
    }
    .section-prefix { color: var(--green); font-weight: 700; font-size: 1.2rem; }
    .section-title {
      font-family: var(--heading-font);
      font-size: 1.3rem; font-weight: 600; color: var(--text);
    }

    /* ABOUT STATS */
    .about-stats { display: flex; gap: 24px; margin-top: 16px; flex-wrap: wrap; }
    .stat-block { background: var(--bg-header); border: 1px solid var(--border); border-radius: 4px; padding: 12px 20px; text-align: center; }
    .stat-value { font-family: var(--heading-font); font-size: 1.4rem; font-weight: 700; color: var(--green); display: block; }
    .stat-label { font-size: 0.7rem; color: var(--muted); text-transform: uppercase; letter-spacing: 1px; }

    /* CODE BLOCK */
    .code-block {
      background: var(--bg-card); border: 1px solid var(--border);
      border-radius: 6px; overflow: hidden; margin-bottom: 16px;
    }
    .code-header {
      background: var(--bg-header); padding: 8px 16px;
      font-size: 0.8rem; color: var(--muted);
      border-bottom: 1px solid var(--border);
      display: flex; justify-content: space-between; align-items: center;
    }
    .code-body { padding: 16px; }

    /* SERVICES - single-col list with dividers */
    .services-list {
      display: flex; flex-direction: column; gap: 0;
    }
    .service-row {
      display: flex; align-items: flex-start; gap: 16px; padding: 20px 16px;
      border-bottom: 1px solid var(--border);
    }
    .service-row:last-child { border-bottom: none; }
    .service-row .service-icon {
      color: var(--green); flex-shrink: 0; margin-top: 2px;
    }
    .service-row .service-icon .icon-char { font-size: 20px; line-height: 1; }
    .service-row .service-text { flex: 1; }
    .service-row .service-text h3 { font-family: var(--heading-font); font-size: 1rem; font-weight: 600; color: var(--text); margin-bottom: 4px; }
    .service-row .service-text .terminal-line { font-size: 0.85rem; }
    .service-price { color: var(--green); font-weight: 600; }

    /* TESTIMONIALS - stacked alternating left/right */
    .testimonials-stack { display: flex; flex-direction: column; gap: 16px; }
    .testimonial-card.code-block { max-width: 70%; }
    .testimonial-card.align-left { align-self: flex-start; }
    .testimonial-card.align-right { align-self: flex-end; }
    .testimonial-role { color: var(--border); font-size: 0.75rem; }
    .testimonial-stars { margin-top: 8px; }

    /* GALLERY */
    .gallery-grid {
      display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
      gap: 12px;
    }
    .gallery-item img {
      width: 100%; aspect-ratio: 1; object-fit: cover;
      border: 1px solid var(--border); border-radius: 4px;
      filter: saturate(0.8);
      transition: filter 0.3s;
    }
    .gallery-item img:hover { filter: saturate(1); }

    /* FAQ */
    .faq-list { display: flex; flex-direction: column; gap: 8px; }
    .faq-item { border-radius: 6px; }
    .faq-question {
      cursor: pointer; list-style: none;
      display: flex; align-items: center; gap: 8px;
    }
    .faq-question::-webkit-details-marker { display: none; }
    .faq-answer { color: var(--muted); line-height: 1.7; }

    /* PRICING */
    .price-table { }
    .price-row {
      display: flex; align-items: baseline; gap: 8px;
      margin-bottom: 4px;
    }
    .price-name { color: var(--text); font-weight: 600; white-space: nowrap; }
    .price-sep { color: var(--border); flex: 1; overflow: hidden; }
    .price-amount { color: var(--green); font-weight: 700; white-space: nowrap; }
    .price-desc {
      color: var(--muted); font-size: 0.8rem; margin-bottom: 12px;
      padding-left: 16px;
    }

    /* CONTACT - full-width stacked */
    .contact-grid {
      display: flex; flex-direction: column; gap: 24px;
    }
    .map-link { color: var(--blue); text-decoration: none; }
    .map-link:hover { text-decoration: underline; }
    .contact-form { }
    .form-group { margin-bottom: 12px; }
    .form-label {
      display: block; font-size: 0.8rem; color: var(--green);
      margin-bottom: 4px;
    }
    .form-input, .form-textarea {
      width: 100%; padding: 10px 14px;
      background: var(--bg); border: 1px solid var(--border);
      color: var(--text); font-family: var(--body-font); font-size: 0.85rem;
      border-radius: 4px;
    }
    .form-input:focus, .form-textarea:focus {
      outline: none; border-color: var(--green);
    }
    .form-textarea { resize: vertical; }
    .form-btn {
      padding: 10px 24px; background: var(--green); color: var(--bg);
      border: none; font-family: var(--heading-font); font-size: 0.85rem;
      font-weight: 700; cursor: pointer; border-radius: 4px;
      transition: background 0.2s;
    }
    .form-btn:hover { background: #16a34a; }

    /* FOOTER */
    .site-footer {
      border-top: 1px solid var(--border);
      margin-top: 32px; padding: 32px 24px;
    }
    .footer-inner {
      max-width: 900px; margin: 0 auto; text-align: center;
    }
    .social-links {
      display: flex; justify-content: center; gap: 16px;
      margin: 12px 0;
    }
    .social-link {
      color: var(--muted); text-decoration: none; font-size: 0.8rem;
      transition: color 0.2s;
    }
    .social-link:hover { color: var(--green); }
    .footer-copy {
      font-size: 0.75rem; color: var(--border); margin-top: 8px;
    }

    /* RESPONSIVE */
    @media (max-width: 768px) {
      .nav-links { display: none; flex-direction: column; position: absolute; top: 100%; left: 0; right: 0; background: var(--bg-header); padding: 16px 24px; gap: 16px; border-bottom: 1px solid var(--border); }
      .nav-links.show { display: flex; }
      .nav-toggle { display: block; }
      .hero-headline { font-size: 1.4rem; }
      .testimonial-card.code-block { max-width: 100%; }
      .gallery-grid { grid-template-columns: repeat(2, 1fr); }
      .about-stats { flex-direction: column; }
    }

    @media (max-width: 480px) {
      body { font-size: 13px; }
      .hero-headline { font-size: 1.2rem; }
      .section-title { font-size: 1.1rem; }
      .nav-links { gap: 8px; flex-wrap: wrap; }
      .terminal-body { padding: 16px 12px; }
    }
  </style>
</head>
<body>
  <nav class="site-nav">
    <div class="nav-inner">
      <a href="#" class="nav-brand">${bSafe}</a>
      <button class="nav-toggle" aria-label="Toggle menu" onclick="this.parentElement.querySelector('.nav-links').classList.toggle('show')">
        <span></span><span></span><span></span>
      </button>
      <div class="nav-links">${navHtml}</div>
    </div>
  </nav>
  ${promotionHtml}
  ${heroHtml}
  ${aboutHtml}
  ${servicesHtml}
  ${galleryHtml}
  ${testimonialsHtml}
  ${faqHtml}
  ${priceHtml}
  ${hoursHtml}
  ${contactHtml}
  ${footerHtml}
</body>
</html>`;
  },
};
